'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import Button from '@/components/button';
import CustomSortingIcon from '@/components/icons/customSortingIcon';
import { ExpandLessIcon, ExpandMoreIcon } from '@/components/ui/icons';
import { Menu } from '@/components/ui/menu';
import { ProductSortingOptions } from '@/features/products/components/list/filters/productSortingOptions';
import {
  useProductListActiveSortingOption,
  useProductListSortingOptions
} from '@/features/products/state/list/context';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { listingTranslate } from '@/i18n/translations/listingTranslations';

const ProductSortingDrawer = dynamic(
  () =>
    import('@/features/products/components/list/filters/productSortingDrawer').then(
      (m) => m.ProductSortingDrawer
    ),
  { ssr: false }
);

export const ProductSortingButton = ({ classList }: { classList?: string }) => {
  const isDesktop = useIsDesktop();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSortingDrawerActive, setIsSortingDrawerActive] = useState(false);
  const sortingOptions = useProductListSortingOptions();
  const activeSortingOption = useProductListActiveSortingOption();

  const activeSortingLabel = sortingOptions.find(
    (option) => option.value === activeSortingOption
  )?.label;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setIsSortingDrawerActive(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsSortingDrawerActive(false);
  };

  const sortingMenuOpen = Boolean(anchorEl);

  return (
    <div className={`flex-col items-end ${classList}`}>
      <Button
        className="bg-(--mui-palette-common-white_states-main) rounded-lg text-left justify-start shadow-xs p-2"
        onClick={handleClick}
        startIcon={
          <CustomSortingIcon className="size-6 m-1.5 text-(--mui-palette-primary-light) mr-0" />
        }
        endIcon={
          <div className="hidden md:block">
            {sortingMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </div>
        }
      >
        <p className="capitalize font-bold text-sm md:text-base text-(--mui-palette-text-primary)">
          {listingTranslate('sorting')}
          <span className="font-normal normal-case hidden lg:inline-block">
            <b>:</b> {activeSortingLabel}
          </span>
        </p>
      </Button>
      {isDesktop ? (
        <Menu
          anchorEl={anchorEl}
          open={sortingMenuOpen}
          onClose={handleClose}
          className="z-10 shadow-md rounded-md min-w-(--anchor-width)"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          {/* Narrower gutters so the menu fits within the button's width (it shares its right edge). */}
          <ProductSortingOptions callback={handleClose} className="px-2 py-4" />
        </Menu>
      ) : (
        <ProductSortingDrawer
          isActive={isSortingDrawerActive}
          onClose={() => setIsSortingDrawerActive(false)}
        />
      )}
    </div>
  );
};
